import { useEffect, useRef, useState } from 'react';
import { Toggle } from '@/components/ui';
import { previewCue } from '@/lib/feedback';
import { useAudioState } from '@/lib/useAudioUnlock';
import { useSettings } from '@/store/settings';

/**
 * The mute control, in the screen header.
 *
 * Here rather than buried in a settings page because the real need is "the baby
 * is asleep, kill it now" — that has to be one tap from the table, and the header
 * is sticky so the popover never covers the felt. Emphatically not part of
 * `GameSettings`, which is per-game rules sent to the server.
 */
export default function FeedbackButton() {
  const sound = useSettings((state) => state.sound);
  const vibration = useSettings((state) => state.vibration);
  const setSound = useSettings((state) => state.setSound);
  const setVibration = useSettings((state) => state.setVibration);
  const state = useAudioState();

  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  // Tap anywhere else to dismiss.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const label = sound ? 'Geluid aan' : 'Geluid uit';

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={label}
        aria-expanded={open}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-300 hover:bg-base-800"
      >
        <SpeakerIcon muted={!sound} />
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-30 w-72 rounded-2xl border border-white/10 bg-base-800 p-3 shadow-xl">
          <Toggle
            checked={sound}
            onChange={(next) => {
              setSound(next);
              // This tap *is* a user gesture, so switching sound on doubles as
              // the unlock retry and as proof to the player that it works.
              if (next) previewCue({ kind: 'your-turn' });
            }}
            label="Geluid"
            hint="Kaarten, slagen en de puntentelling."
          />
          <Toggle
            checked={vibration}
            onChange={(next) => {
              setVibration(next);
              if (next) navigator.vibrate?.(12);
            }}
            label="Trilling"
            hint={
              typeof navigator !== 'undefined' && !('vibrate' in navigator)
                ? 'Dit toestel ondersteunt geen trilling (iPhone kan het niet).'
                : 'Korte tik bij je beurt en bij het spelen.'
            }
          />

          {sound && state !== 'running' && (
            <p className="mt-2 rounded-xl bg-amber-400/10 px-3 py-2 text-sm text-amber-100">
              {state === 'unavailable'
                ? 'Deze browser kan geen geluid maken.'
                : 'Tik ergens op het scherm om het geluid te starten.'}
            </p>
          )}

          <p className="mt-2 text-xs text-slate-500">
            Hoor je niets op een iPhone? Controleer het schuifje aan de zijkant — dat dempt ook
            spelgeluid.
          </p>
        </div>
      )}
    </div>
  );
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 5 6 9H3v6h3l5 4V5z" strokeLinecap="round" strokeLinejoin="round" />
      {muted ? (
        <path d="M16 9l5 6M21 9l-5 6" strokeLinecap="round" />
      ) : (
        <path d="M15.5 8.5a5 5 0 010 7M18.5 6a8 8 0 010 12" strokeLinecap="round" />
      )}
    </svg>
  );
}
